import { IParseEditCreatorsGoogleSheetRequest } from "@shared/lib/types/brief";
import {
  IBriefDeliverableResponse,
  IFieldConfig,
} from "@shared/lib/types/briefInfluencerV2";
import { IDeliverableConfigurator } from "@shared/lib/types/deliverableConfigurator";
import { AlertCircle, CheckCircle2 } from "lucide-react";
import { useMemo } from "react";
import styles from "../../BulkAddModal/process.module.scss";
import { isProcessedEditRow } from "../lib/constants";
import { ErrorList } from "./ErrorList";
import { ProcessedTable } from "./ProcessedTable";

export type ReviewTab = "processed" | "error";

interface ProcessStepProps {
  rows: IParseEditCreatorsGoogleSheetRequest[];
  deliverables: IBriefDeliverableResponse[];
  configurators: IDeliverableConfigurator[];
  customFields: IFieldConfig[];
  activeTab: ReviewTab;
  onTabChange: (tab: ReviewTab) => void;
}

export function ProcessStep({
  rows,
  deliverables,
  configurators,
  customFields,
  activeTab,
  onTabChange,
}: ProcessStepProps) {
  const processedRows = useMemo(
    () => rows.filter(isProcessedEditRow),
    [rows]
  );
  const errorRows = useMemo(
    () => rows.filter((row) => !isProcessedEditRow(row)),
    [rows]
  );
  const withDeliverables = processedRows.filter((row) =>
    Boolean(row.editDeliverablesV2Id)
  ).length;
  const withExtraFields = processedRows.filter(
    (row) => Object.keys(row.editedDataFields ?? {}).length > 0
  ).length;

  return (
    <div className={styles.processBody}>
      <div className={styles.tabCards}>
        <button
          type="button"
          onClick={() => onTabChange("processed")}
          className={`${styles.tabCard} ${
            activeTab === "processed" ? styles.tabCardProcessedActive : ""
          }`}
        >
          <div className={`${styles.tabCardIcon} ${styles.tabCardIconSuccess}`}>
            <CheckCircle2 size={18} />
          </div>
          <div>
            <div className={styles.tabCardCountSuccess}>
              {processedRows.length}
            </div>
            <div className={styles.tabCardLabelSuccess}>Ready to update</div>
          </div>
        </button>
        <button
          type="button"
          onClick={() => onTabChange("error")}
          className={`${styles.tabCard} ${
            activeTab === "error" ? styles.tabCardErrorActive : ""
          }`}
        >
          <div className={`${styles.tabCardIcon} ${styles.tabCardIconError}`}>
            <AlertCircle size={18} />
          </div>
          <div>
            <div className={styles.tabCardCountError}>{errorRows.length}</div>
            <div className={styles.tabCardLabelError}>Couldn’t import</div>
          </div>
        </button>
      </div>

      <p className={styles.processSummary}>
        {processedRows.length > 0 ? (
          <>
            <span className={styles.processSummaryStrong}>
              {processedRows.length} creator
              {processedRows.length !== 1 ? "s" : ""}
            </span>
            {withDeliverables > 0 && (
              <> · {withDeliverables} with deliverables</>
            )}
            {withExtraFields > 0 && (
              <> · {withExtraFields} with extra fields</>
            )}
          </>
        ) : (
          "No rows passed validation. Check who failed and why below."
        )}
      </p>

      <div className={styles.processHeading}>
        <h4>
          {activeTab === "processed"
            ? "Creators that will be updated"
            : "Why these rows failed"}
        </h4>
        {activeTab === "processed" && processedRows.length > 0 && (
          <span>Review deliverables and fields before confirming</span>
        )}
      </div>

      <div className={styles.tableWrap}>
        {activeTab === "processed" ? (
          <ProcessedTable
            rows={processedRows}
            customFields={customFields}
            deliverables={deliverables}
            configurators={configurators}
            onViewErrors={() => onTabChange("error")}
          />
        ) : (
          <ErrorList rows={errorRows} />
        )}
      </div>
    </div>
  );
}
